import React, { useEffect, useRef, useState } from "react";
import {
	Button,
	Checkbox,
	FormControlLabel,
	IconButton,
	MenuItem,
	Radio,
	RadioGroup,
	Select,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableRow,
	TextField,
	Tooltip,
	makeStyles,
} from "@material-ui/core";
import Alert from "@material-ui/lab/Alert";
import AddIcon from "@material-ui/icons/Add";
import BuildIcon from "@material-ui/icons/Build";
import CalculateIcon from "@material-ui/icons/Functions";
import ChevronRightIcon from "@material-ui/icons/ChevronRight";
import CreditCardIcon from "@material-ui/icons/CreditCard";
import DeleteIcon from "@material-ui/icons/Delete";
import EventIcon from "@material-ui/icons/Event";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import OpenInNewIcon from "@material-ui/icons/OpenInNew";
import PictureAsPdfIcon from "@material-ui/icons/PictureAsPdf";
import SearchIcon from "@material-ui/icons/Search";
import SettingsIcon from "@material-ui/icons/Settings";
import WarningIcon from "@material-ui/icons/Warning";
import WhatsAppIcon from "@material-ui/icons/WhatsApp";

import {
	formatarDataHora,
	formatarMoeda,
	formatarQtd,
	numeroDigitado,
	paraCampoData,
	paraCampoDataHora,
	textoPagamento,
	valorAlterado,
} from "../../helpers/itensLead";
import { SelecaoBusca, SelecaoMultiplaBusca } from "./SelecaoBusca";

// Visual da aba Itens do lead no ERP (app/crm/lead/style.css, seção "Aba Itens"): as cores do
// resumo são as mesmas, com a variante do modo escuro.
export const useStyles = makeStyles((theme) => {
	const escuro = theme.palette.type === "dark";
	const cor = {
		valor: escuro ? "#93a8ff" : "#2736e1",
		desconto: escuro ? "#f87171" : "#dc2626",
		total: escuro ? "#4ade80" : "#15803d",
		totalFundo: escuro ? "rgba(22, 163, 74, 0.16)" : "#dcfce7",
		acrescimo: escuro ? "#fb923c" : "#c2410c",
		excedenteFundo: escuro ? "rgba(220, 38, 38, 0.18)" : "#fee2e2",
	};
	return {
		bloco: {
			background: escuro ? "rgba(255, 255, 255, 0.03)" : "#f8f9fb",
			border: `1px solid ${theme.palette.divider}`,
			borderRadius: 10,
			padding: "12px 14px",
			marginBottom: 12,
		},
		titulo: {
			display: "flex",
			alignItems: "center",
			gap: 6,
			fontSize: 13,
			fontWeight: 700,
			textTransform: "uppercase",
			letterSpacing: "0.06em",
			paddingBottom: 8,
			marginBottom: 10,
			borderBottom: `1px solid ${theme.palette.divider}`,
			"& svg": { color: theme.palette.primary.main, fontSize: 18 },
		},
		linha: {
			display: "flex",
			flexWrap: "wrap",
			alignItems: "flex-end",
			gap: 10,
			margin: "6px 0",
		},
		campo: { minWidth: 0, display: "flex", flexDirection: "column" },
		campoLargo: { flex: "1 1 220px" },
		campoGrupo: { flex: "0 1 200px", minWidth: 150 },
		campoQtd: { flex: "0 0 110px" },
		rotulo: { fontSize: 12, fontWeight: 600, marginBottom: 3, opacity: 0.85 },
		controle: { width: "100%", background: theme.palette.background.paper },
		radioLinha: { display: "flex", alignItems: "center", minHeight: 38 },
		opcoes: {
			display: "flex",
			flexWrap: "wrap",
			gap: "0 24px",
			margin: "2px 0 6px",
			"& .MuiFormControlLabel-label": { fontSize: 13 },
		},
		pesquisarPor: {
			margin: "0 0 4px",
			"& .MuiFormControlLabel-label": { fontSize: 13 },
		},
		botaoAdicionar: { height: 40, minWidth: 130, whiteSpace: "nowrap" },
		dica: { display: "block", marginTop: 4, fontSize: 11, opacity: 0.7 },
		tabela: {
			overflowX: "auto",
			marginTop: 8,
			"& th": { whiteSpace: "nowrap", fontWeight: 700, fontSize: 12, padding: "6px 8px" },
			"& td": { fontSize: 13, padding: "4px 8px" },
		},
		colToggle: { width: 28, padding: "0 2px !important" },
		colNome: { minWidth: 160 },
		inputPreco: { width: 115, background: theme.palette.background.paper },
		inputQtd: { width: 80, background: theme.palette.background.paper },
		linhaExcedente: { "& td": { background: cor.excedenteFundo } },
		excedente: { color: cor.desconto, fontWeight: 700, fontSize: 11, marginLeft: 4 },
		promocao: { color: cor.total, fontWeight: 700, marginLeft: 4 },
		seloDesconto: { color: cor.desconto, fontWeight: 700, marginLeft: 4 },
		seloAcrescimo: { color: cor.acrescimo, fontWeight: 700, marginLeft: 4 },
		semEstoque: { color: cor.desconto, fontSize: 18, verticalAlign: "middle", marginLeft: 4 },
		vazio: { textAlign: "center", opacity: 0.7, padding: 12 },
		obs: {
			background: theme.palette.background.paper,
			"& .MuiOutlinedInput-root": { borderLeft: `3px solid ${theme.palette.primary.main}` },
		},
		lucro: { textAlign: "right", color: cor.desconto, fontWeight: 700, marginBottom: 6 },
		resumo: {
			display: "grid",
			gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
			gap: "8px 24px",
			fontSize: 14,
		},
		resumoCabecalho: {
			textAlign: "center",
			textTransform: "uppercase",
			fontWeight: 700,
			borderBottom: `2px solid ${theme.palette.divider}`,
			marginBottom: 4,
		},
		resumoLinha: {
			display: "flex",
			justifyContent: "space-between",
			gap: 8,
			padding: "3px 0",
			borderBottom: `1px dashed ${theme.palette.divider}`,
		},
		valor: { color: cor.valor },
		desconto: { color: cor.desconto },
		acrescimo: { color: cor.acrescimo },
		total: { color: cor.total, fontWeight: 700 },
		pagamentoResumo: { marginTop: 10, fontWeight: 600, opacity: 0.85 },
		totalGeral: {
			display: "flex",
			justifyContent: "space-between",
			marginTop: 12,
			padding: "10px 14px",
			borderRadius: 8,
			background: cor.totalFundo,
			color: cor.total,
			fontSize: 18,
			fontWeight: 700,
		},
		ultimaCotacao: { fontSize: 13, margin: "8px 0", display: "flex", alignItems: "center", flexWrap: "wrap", gap: 4 },
		vencida: { color: cor.desconto, fontWeight: 700 },
		acoes: { display: "flex", flexWrap: "wrap", gap: 10, marginTop: 10 },
		botaoVerde: {
			background: "#2e7d32",
			color: "#fff",
			"&:hover": { background: "#1b5e20" },
		},
		// usados pelas janelas de resultado (index.js)
		resultados: {
			maxHeight: 400,
			overflowY: "auto",
			"& th": { whiteSpace: "nowrap", fontWeight: 700, fontSize: 12, padding: "6px 8px" },
			"& td": { fontSize: 12, padding: "4px 8px" },
		},
		estoqueGeral: { color: "#00AEEF" },
		estoqueReservado: { color: "#9d9d9d", fontWeight: 700 },
		estoqueDisponivel: { color: "#2B8717", fontWeight: 700 },
		estoqueZerado: { color: cor.desconto, fontWeight: 700 },
		filtroResultados: { marginBottom: 8 },
		avisoAcao: { marginTop: 10, whiteSpace: "pre-line" },
	};
});

const Titulo = ({ icone, children }) => {
	const classes = useStyles();
	return (
		<div className={classes.titulo}>
			{icone}
			{children}
		</div>
	);
};

const Campo = ({ rotulo, className, children }) => {
	const classes = useStyles();
	return (
		<div className={`${classes.campo} ${className || ""}`}>
			<span className={classes.rotulo}>{rotulo}</span>
			{children}
		</div>
	);
};

// O texto do campo é dele enquanto ele tem o foco; fora do foco, volta ao que o ERP gravou a cada resposta
// (inclusive quando o ERP corrige ou recusa). A gravação do blur não trava a tela, e sem isso a resposta dela
// apagaria o que o usuário já digita no campo seguinte.
const useTextoDoCampo = (valor, versao) => {
	const [texto, setTexto] = useState(valor ?? "");
	const focado = useRef(false);
	useEffect(() => {
		if (!focado.current) setTexto(valor ?? "");
	}, [valor, versao]);
	return {
		texto,
		setTexto,
		onFocus: () => { focado.current = true; },
		sair: () => { focado.current = false; },
	};
};

// Grava no blur, só quando o número mudou.
const CampoNumero = ({ valor, versao, onGravar, disabled, className, step, prefixo }) => {
	const campo = useTextoDoCampo(valor, versao);
	return (
		<TextField
			value={campo.texto}
			type="number"
			size="small"
			variant="outlined"
			disabled={disabled}
			className={className}
			inputProps={{ step: step || "any", min: 0 }}
			InputProps={prefixo ? { startAdornment: <span style={{ marginRight: 4, opacity: 0.7 }}>{prefixo}</span> } : undefined}
			onFocus={campo.onFocus}
			onChange={(e) => campo.setTexto(e.target.value)}
			onBlur={(e) => {
				campo.sair();
				if (valorAlterado(valor, e.target.value)) onGravar(numeroDigitado(e.target.value));
			}}
			onKeyDown={(e) => e.key === "Enter" && e.target.blur()}
		/>
	);
};

const CampoObservacao = ({ valor, versao, onGravar, disabled }) => {
	const classes = useStyles();
	const campo = useTextoDoCampo(valor, versao);
	return (
		<TextField
			value={campo.texto}
			placeholder="Observação do item"
			size="small"
			variant="outlined"
			multiline
			minRows={2}
			fullWidth
			className={classes.obs}
			disabled={disabled}
			onFocus={campo.onFocus}
			onChange={(e) => campo.setTexto(e.target.value)}
			onBlur={(e) => {
				campo.sair();
				if ((e.target.value || "") !== (valor || "")) onGravar(e.target.value);
			}}
		/>
	);
};

// Como o rowToggler do ERP: ">" fechada, seta para baixo aberta.
const Alternar = ({ aberto, onClick }) => (
	<IconButton size="small" onClick={onClick}>
		{aberto ? <ExpandMoreIcon fontSize="small" /> : <ChevronRightIcon fontSize="small" />}
	</IconButton>
);

// Gravação disparada pelo blur de um campo: vai na fila sem desabilitar os botões (ver executar no index).
const NO_BLUR = { emSegundoPlano: true };

const passoQtd = (casas) => (casas > 0 ? String(1 / Math.pow(10, casas)) : "1");

// ---- Pagamento ----

export const SecaoPagamento = ({ estado, ocupado, executar }) => {
	const classes = useStyles();
	const pagamento = estado.pagamento;
	const tabela = pagamento.tabelaPreco;
	return (
		<div className={classes.bloco}>
			<Titulo icone={<CreditCardIcon />}>Pagamento</Titulo>
			<div className={classes.linha}>
				<Campo rotulo="Tipo de pagamento:">
					<RadioGroup
						row
						className={classes.radioLinha}
						value={pagamento.forma || ""}
						onChange={(e) => executar("formaPagamento", { forma: e.target.value })}
					>
						<FormControlLabel value="A" control={<Radio size="small" color="primary" disabled={ocupado} />} label="À vista" />
						<FormControlLabel value="P" control={<Radio size="small" color="primary" disabled={ocupado} />} label="A prazo" />
					</RadioGroup>
				</Campo>

				<Campo rotulo="Plano de pagamento:" className={classes.campoLargo}>
					<SelecaoBusca
						className={classes.controle}
						opcoes={pagamento.planos}
						valor={pagamento.planoId}
						placeholder="-- Selecione --"
						disabled={ocupado || !pagamento.forma}
						onChange={(planoId) => planoId !== pagamento.planoId && executar("plano", { planoId })}
					/>
				</Campo>

				<Campo rotulo="Parcelas:" className={classes.campoQtd}>
					<CampoNumero
						valor={pagamento.qtdParcelas}
						versao={estado.versao}
						step="1"
						className={classes.controle}
						disabled={ocupado || pagamento.bloquearParcelas}
						onGravar={(qtdParcelas) => executar("parcelas", { qtdParcelas }, NO_BLUR)}
					/>
				</Campo>
			</div>

			{tabela.ativa && (
				<div className={classes.linha}>
					<Campo rotulo="Tabela de preço:" className={classes.campoLargo}>
						<Select
							variant="outlined"
							margin="dense"
							className={classes.controle}
							disabled={ocupado || tabela.travada}
							value={tabela.id || ""}
							onChange={(e) => executar("tabelaPreco", { tabelaPrecoId: e.target.value })}
						>
							{tabela.tabelas.map((t) => (
								<MenuItem key={t.id} value={t.id}>{t.nome}</MenuItem>
							))}
						</Select>
						{tabela.travada && <span className={classes.dica}>* Remova as peças para trocar a tabela.</span>}
					</Campo>
				</div>
			)}
		</div>
	);
};

// ---- Peças ----

const SelosPeca = ({ item }) => {
	const classes = useStyles();
	return (
		<>
			{item.excedeLimite && (
				<Tooltip title="O desconto passa do seu limite. A liberação por senha é feita pelo lead no ERP.">
					<span className={classes.excedente}>(acima do limite de desconto)</span>
				</Tooltip>
			)}
			{item.promocao && (
				<Tooltip title={item.promocao}>
					<span className={classes.promocao}>(promoção)</span>
				</Tooltip>
			)}
			{item.ajustePlano && (
				<span className={item.ajustePlanoDesconto ? classes.seloDesconto : classes.seloAcrescimo}>
					({item.ajustePlano})
				</span>
			)}
		</>
	);
};

export const SecaoPecas = ({ estado, ocupado, executar, busca, setBusca, onBuscar, onEstoqueLojas }) => {
	const classes = useStyles();
	const [abertos, setAbertos] = useState({});
	const p = estado.permissoes;
	const alternar = (id) => setAbertos((a) => ({ ...a, [id]: !a[id] }));
	const colunas = 8 + (p.codigoFabricante ? 1 : 0);

	return (
		<div className={classes.bloco}>
			<Titulo icone={<SettingsIcon />}>Peças</Titulo>

			{p.lancarPeca && (
				<>
					{p.filtroPesquisaProduto && (
						<Campo rotulo="Pesquisar Por:">
							<RadioGroup
								row
								className={classes.pesquisarPor}
								value={busca.pesquisa}
								onChange={(e) => setBusca({ ...busca, pesquisa: e.target.value })}
							>
								{estado.pesquisas.map((ps) => (
									<FormControlLabel key={ps.valor} value={ps.valor}
										control={<Radio size="small" color="primary" />} label={ps.descricao} />
								))}
							</RadioGroup>
						</Campo>
					)}

					<div className={classes.opcoes}>
						<FormControlLabel
							control={<Checkbox size="small" color="primary" checked={busca.apenasComEstoque}
								onChange={(e) => setBusca({ ...busca, apenasComEstoque: e.target.checked })} />}
							label="Apenas produtos com estoque disponível"
						/>
						{p.estoqueFiliais && (
							<FormControlLabel
								control={<Checkbox size="small" color="primary" checked={busca.consultarLojas}
									onChange={(e) => setBusca({ ...busca, consultarLojas: e.target.checked })} />}
								label="Consultar com o estoque de outras lojas"
							/>
						)}
					</div>

					<div className={classes.linha}>
						<Campo rotulo="Grupo:" className={classes.campoGrupo}>
							<SelecaoBusca
								className={classes.controle}
								opcoes={estado.grupos}
								valor={busca.grupoId || null}
								placeholder="Todos os grupos"
								disabled={ocupado}
								onChange={(grupoId) => setBusca({ ...busca, grupoId: grupoId || "" })}
							/>
						</Campo>
						<Campo rotulo="Peça:" className={classes.campoLargo}>
							<TextField
								size="small"
								variant="outlined"
								className={classes.controle}
								placeholder="Digite e tecle Enter para buscar"
								disabled={ocupado}
								value={busca.termo}
								onChange={(e) => setBusca({ ...busca, termo: e.target.value })}
								onKeyDown={(e) => e.key === "Enter" && onBuscar()}
								InputProps={{ endAdornment: <SearchIcon fontSize="small" style={{ opacity: 0.5 }} /> }}
							/>
						</Campo>
						<Campo rotulo="Quantidade:" className={classes.campoQtd}>
							<TextField
								type="number"
								size="small"
								variant="outlined"
								className={classes.controle}
								disabled={ocupado}
								value={busca.qtd}
								inputProps={{ min: 0, step: "any" }}
								onChange={(e) => setBusca({ ...busca, qtd: e.target.value })}
							/>
						</Campo>
						<Button variant="contained" color="primary" startIcon={<AddIcon />}
							className={classes.botaoAdicionar} disabled={ocupado} onClick={onBuscar}>
							Adicionar
						</Button>
					</div>
				</>
			)}

			<div className={classes.tabela}>
				<Table size="small">
					<TableHead>
						<TableRow>
							<TableCell className={classes.colToggle} />
							<TableCell align="center">ID</TableCell>
							{p.codigoFabricante && <TableCell>Cód. fab.</TableCell>}
							<TableCell>Produto</TableCell>
							<TableCell>{p.custoVenda ? "Custo / venda" : "Venda"}</TableCell>
							<TableCell>Preço unit.</TableCell>
							<TableCell>Qtd.</TableCell>
							<TableCell>Subtotal</TableCell>
							<TableCell />
						</TableRow>
					</TableHead>
					<TableBody>
						{estado.pecas.length === 0 && (
							<TableRow>
								<TableCell colSpan={colunas} className={classes.vazio}>Não há peças relacionadas no lead</TableCell>
							</TableRow>
						)}
						{estado.pecas.map((item) => (
							<React.Fragment key={item.id}>
								<TableRow className={item.excedeLimite ? classes.linhaExcedente : undefined}>
									<TableCell className={classes.colToggle}>
										<Alternar aberto={abertos[item.id]} onClick={() => alternar(item.id)} />
									</TableCell>
									<TableCell align="center">
										{item.prodId}
										{item.semEstoque && (
											<Tooltip title="Não há estoque suficiente para faturamento">
												<WarningIcon className={classes.semEstoque} />
											</Tooltip>
										)}
									</TableCell>
									{p.codigoFabricante && <TableCell>{item.codigoFabricante}</TableCell>}
									<TableCell className={classes.colNome}>
										{item.nome}
										<SelosPeca item={item} />
									</TableCell>
									<TableCell style={{ whiteSpace: "nowrap" }}>
										{p.custoVenda && `${formatarMoeda(item.custo)} / `}
										{formatarMoeda(item.precoReferencia)}
									</TableCell>
									<TableCell>
										<CampoNumero
											valor={item.preco}
											versao={estado.versao}
											prefixo="R$"
											step="0.01"
											className={classes.inputPreco}
											disabled={ocupado || !item.podeEditarPreco}
											onGravar={(preco) => executar("editarPeca", { id: item.id, preco }, NO_BLUR)}
										/>
									</TableCell>
									<TableCell>
										<CampoNumero
											valor={item.qtd}
											versao={estado.versao}
											step={passoQtd(item.casasQtd)}
											className={classes.inputQtd}
											disabled={ocupado || !item.podeEditarQtd}
											onGravar={(qtd) => executar("editarPeca", { id: item.id, qtd }, NO_BLUR)}
										/>
									</TableCell>
									<TableCell style={{ whiteSpace: "nowrap" }}>{formatarMoeda(item.subtotal)}</TableCell>
									<TableCell style={{ whiteSpace: "nowrap" }} align="center">
										{p.estoqueFiliais && (
											<Tooltip title="Visualizar estoque das filiais">
												<IconButton size="small" color="primary" disabled={ocupado} onClick={() => onEstoqueLojas(item.prodId)}>
													<SearchIcon fontSize="small" />
												</IconButton>
											</Tooltip>
										)}
										{item.podeRemover && (
											<Tooltip title="Remover peça">
												<IconButton size="small" disabled={ocupado}
													onClick={() => executar("removerPeca", { id: item.id })}>
													<DeleteIcon fontSize="small" color="error" />
												</IconButton>
											</Tooltip>
										)}
									</TableCell>
								</TableRow>
								{abertos[item.id] && (
									<TableRow>
										<TableCell colSpan={colunas}>
											<CampoObservacao
												valor={item.observacao}
												versao={estado.versao}
												disabled={ocupado}
												onGravar={(observacao) => executar("editarPeca", { id: item.id, observacao }, NO_BLUR)}
											/>
										</TableCell>
									</TableRow>
								)}
							</React.Fragment>
						))}
					</TableBody>
				</Table>
			</div>
		</div>
	);
};

// ---- Serviços ----

export const SecaoServicos = ({ estado, ocupado, executar, onBuscarServicos }) => {
	const classes = useStyles();
	const [abertos, setAbertos] = useState({});
	const [filtro, setFiltro] = useState("TODOS");
	const [termo, setTermo] = useState("");
	const [marcados, setMarcados] = useState([]);
	const [servicoId, setServicoId] = useState("");
	const [qtd, setQtd] = useState("1");
	const p = estado.permissoes;
	const alternar = (id) => setAbertos((a) => ({ ...a, [id]: !a[id] }));

	const adicionarSelecionados = async () => {
		const servicos = p.campoMultiplosServicos
			? marcados.map((id) => ({ id, qtd: 1 }))
			: servicoId ? [{ id: servicoId, qtd: numeroDigitado(qtd) }] : [];
		const r = await executar("adicionarServicos", { servicos });
		if (r) {
			setMarcados([]);
			setServicoId("");
			setQtd("1");
		}
	};

	const buscar = async () => {
		const ok = await onBuscarServicos(termo, filtro);
		if (ok) setTermo("");
	};

	const rotuloServico = (s) => (s ? `${s.nome} - ${formatarMoeda(s.preco)}` : "");

	return (
		<div className={classes.bloco}>
			<Titulo icone={<BuildIcon />}>Serviços</Titulo>

			{p.lancarServico && p.filtroPesquisaServico && (
				<>
					<RadioGroup row className={classes.opcoes} value={filtro} onChange={(e) => setFiltro(e.target.value)}>
						{[["TODOS", "Todos"], ["ID", "ID"], ["NOME", "Nome"], ["GRUPO", "Grupo"]].map(([v, l]) => (
							<FormControlLabel key={v} value={v} control={<Radio size="small" color="primary" />} label={l} />
						))}
					</RadioGroup>
					<div className={classes.linha}>
						<Campo rotulo="Serviço:" className={classes.campoLargo}>
							<TextField
								size="small"
								variant="outlined"
								className={classes.controle}
								disabled={ocupado}
								value={termo}
								onChange={(e) => setTermo(e.target.value)}
								onKeyDown={(e) => e.key === "Enter" && buscar()}
								InputProps={{ endAdornment: <SearchIcon fontSize="small" style={{ opacity: 0.5 }} /> }}
							/>
						</Campo>
						<Button variant="contained" color="primary" startIcon={<AddIcon />}
							className={classes.botaoAdicionar} disabled={ocupado} onClick={buscar}>
							Adicionar
						</Button>
					</div>
				</>
			)}

			{p.lancarServico && !p.filtroPesquisaServico && (
				<div className={classes.linha}>
					<Campo rotulo={p.campoMultiplosServicos ? "Serviços:" : "Serviço:"} className={classes.campoLargo}>
						{p.campoMultiplosServicos ? (
							<SelecaoMultiplaBusca
								className={classes.controle}
								opcoes={estado.servicosDisponiveis}
								valores={marcados}
								rotulo={rotuloServico}
								placeholder="Selecione os serviços"
								disabled={ocupado}
								onChange={setMarcados}
							/>
						) : (
							<SelecaoBusca
								className={classes.controle}
								opcoes={estado.servicosDisponiveis}
								valor={servicoId || null}
								rotulo={rotuloServico}
								placeholder="Selecione o serviço"
								disabled={ocupado}
								onChange={(id) => setServicoId(id || "")}
							/>
						)}
					</Campo>
					{!p.campoMultiplosServicos && (
						<Campo rotulo="Quantidade:" className={classes.campoQtd}>
							<TextField
								type="number"
								size="small"
								variant="outlined"
								className={classes.controle}
								disabled={ocupado}
								value={qtd}
								inputProps={{ min: 1, step: 1 }}
								onChange={(e) => setQtd(e.target.value)}
							/>
						</Campo>
					)}
					<Button variant="contained" color="primary" startIcon={<AddIcon />}
						className={classes.botaoAdicionar} disabled={ocupado} onClick={adicionarSelecionados}>
						Adicionar
					</Button>
				</div>
			)}

			<div className={classes.tabela}>
				<Table size="small">
					<TableHead>
						<TableRow>
							<TableCell className={classes.colToggle} />
							<TableCell align="center">ID</TableCell>
							<TableCell>Serviço</TableCell>
							<TableCell>Preço base</TableCell>
							<TableCell>Preço unit.</TableCell>
							<TableCell>Qtd.</TableCell>
							<TableCell>Subtotal</TableCell>
							<TableCell />
						</TableRow>
					</TableHead>
					<TableBody>
						{estado.servicos.length === 0 && (
							<TableRow>
								<TableCell colSpan={8} className={classes.vazio}>Não há serviços relacionados no lead</TableCell>
							</TableRow>
						)}
						{estado.servicos.map((item) => (
							<React.Fragment key={item.id}>
								<TableRow className={item.excedeLimite ? classes.linhaExcedente : undefined}>
									<TableCell className={classes.colToggle}>
										<Alternar aberto={abertos[item.id]} onClick={() => alternar(item.id)} />
									</TableCell>
									<TableCell align="center">{item.servId}</TableCell>
									<TableCell className={classes.colNome}>
										{item.nome}
										{item.excedeLimite && <span className={classes.excedente}>(acima do limite de desconto)</span>}
									</TableCell>
									<TableCell style={{ whiteSpace: "nowrap" }}>{formatarMoeda(item.precoBase)}</TableCell>
									<TableCell>
										<CampoNumero
											valor={item.preco}
											versao={estado.versao}
											prefixo="R$"
											step="0.01"
											className={classes.inputPreco}
											disabled={ocupado || !item.podeEditar}
											onGravar={(preco) => executar("editarServico", { id: item.id, preco }, NO_BLUR)}
										/>
									</TableCell>
									<TableCell>
										<CampoNumero
											valor={item.qtd}
											versao={estado.versao}
											step="1"
											className={classes.inputQtd}
											disabled={ocupado || !item.podeEditar}
											onGravar={(qtdNova) => executar("editarServico", { id: item.id, qtd: qtdNova }, NO_BLUR)}
										/>
									</TableCell>
									<TableCell style={{ whiteSpace: "nowrap" }}>{formatarMoeda(item.subtotal)}</TableCell>
									<TableCell align="center">
										{item.podeRemover && (
											<Tooltip title="Remover serviço">
												<IconButton size="small" disabled={ocupado}
													onClick={() => executar("removerServico", { id: item.id })}>
													<DeleteIcon fontSize="small" color="error" />
												</IconButton>
											</Tooltip>
										)}
									</TableCell>
								</TableRow>
								{abertos[item.id] && (
									<TableRow>
										<TableCell colSpan={8}>
											<CampoObservacao
												valor={item.observacao}
												versao={estado.versao}
												disabled={ocupado}
												onGravar={(observacao) => executar("editarServico", { id: item.id, observacao }, NO_BLUR)}
											/>
										</TableCell>
									</TableRow>
								)}
							</React.Fragment>
						))}
					</TableBody>
				</Table>
			</div>
		</div>
	);
};

// ---- Resumo ----

const LinhaResumo = ({ rotulo, valor, classe }) => {
	const classes = useStyles();
	return (
		<div className={`${classes.resumoLinha} ${classe || ""}`}>
			<span>{rotulo}</span>
			<span>{valor}</span>
		</div>
	);
};

export const SecaoResumo = ({ estado }) => {
	const classes = useStyles();
	const r = estado.resumo;
	return (
		<div className={classes.bloco}>
			<Titulo icone={<CalculateIcon />}>Resumo</Titulo>
			{r.lucro !== undefined && (
				<div className={classes.lucro}>Lucro: {formatarMoeda(r.lucro)} ({r.percentualLucro}%)</div>
			)}
			<div className={classes.resumo}>
				{r.mostraPecas && (
					<div>
						<div className={classes.resumoCabecalho}>Produtos</div>
						<LinhaResumo rotulo="Quantidade de peças" valor={formatarQtd(r.qtdPecas, 3)} />
						<LinhaResumo rotulo="Valor em peças (+)" valor={formatarMoeda(r.valorPecas)} classe={classes.valor} />
						<LinhaResumo rotulo="Desconto em peças (-)" valor={formatarMoeda(r.descontoPecas)} classe={classes.desconto} />
						{Number(r.acrescimoPecas) > 0 && (
							<LinhaResumo rotulo="Acréscimo do plano (+)" valor={formatarMoeda(r.acrescimoPecas)} classe={classes.acrescimo} />
						)}
						<LinhaResumo rotulo="Valor total de peças (=)" valor={formatarMoeda(r.totalPecas)} classe={classes.total} />
					</div>
				)}
				{r.mostraServicos && (
					<div>
						<div className={classes.resumoCabecalho}>Serviços</div>
						<LinhaResumo rotulo="Valor em serviços (+)" valor={formatarMoeda(r.valorServicos)} classe={classes.valor} />
						<LinhaResumo rotulo="Desconto em serviços (-)" valor={formatarMoeda(r.descontoServicos)} classe={classes.desconto} />
						<LinhaResumo rotulo="Valor total de serviços (=)" valor={formatarMoeda(r.totalServicos)} classe={classes.total} />
					</div>
				)}
			</div>
			{textoPagamento(estado.pagamento) && (
				<div className={classes.pagamentoResumo}>{textoPagamento(estado.pagamento)}</div>
			)}
			<div className={classes.totalGeral}>
				<span>Total geral (=)</span>
				<span>{formatarMoeda(r.totalGeral)}</span>
			</div>
		</div>
	);
};

// ---- Cotação e agendamento ----

export const SecaoCotacao = ({ estado, ocupado, executar, onGerar, onEnviar, onAgendar, onAbrirCotacao, aviso }) => {
	const classes = useStyles();
	const p = estado.permissoes;
	const ultima = estado.cotacao.ultima;
	const validadeGravada = paraCampoData(estado.cotacao.validade);
	const agendamentoGravado = paraCampoDataHora(estado.dataAgendamento);
	const campoValidade = useTextoDoCampo(validadeGravada, estado.versao);
	const campoAgendamento = useTextoDoCampo(agendamentoGravado, estado.versao);
	const validade = campoValidade.texto;
	const agendamento = campoAgendamento.texto;

	// Cada ação leva a data que é dela, como está na tela: a cotação a validade, o agendamento a data/hora.
	const datasCotacao = () => (validade ? { validadeCotacao: validade } : {});
	const datasAgendamento = () => ({ dataAgendamento: agendamento || null });
	// Sem isso o clique tira o foco do campo, o blur grava e desabilita o botão antes do clique chegar.
	const semRoubarFoco = (e) => e.preventDefault();

	return (
		<div className={classes.bloco}>
			<Titulo icone={<PictureAsPdfIcon />}>Cotação</Titulo>
			<div className={classes.linha}>
				<Campo rotulo="Validade da cotação:">
					<TextField
						type="date"
						size="small"
						variant="outlined"
						className={classes.controle}
						value={validade}
						disabled={ocupado}
						onFocus={campoValidade.onFocus}
						onChange={(e) => campoValidade.setTexto(e.target.value)}
						onBlur={() => {
							campoValidade.sair();
							if (validade && validade !== validadeGravada) executar("dados", { validadeCotacao: validade }, NO_BLUR);
						}}
					/>
				</Campo>
				{p.agendar && (
					<Campo rotulo="Data e hora do agendamento:">
						<TextField
							type="datetime-local"
							size="small"
							variant="outlined"
							className={classes.controle}
							value={agendamento}
							disabled={ocupado}
							onFocus={campoAgendamento.onFocus}
							onChange={(e) => campoAgendamento.setTexto(e.target.value)}
							onBlur={() => {
								campoAgendamento.sair();
								if (agendamento !== agendamentoGravado) executar("dados", { dataAgendamento: agendamento || null }, NO_BLUR);
							}}
						/>
					</Campo>
				)}
			</div>

			{ultima && (
				<div className={classes.ultimaCotacao}>
					<span>
						Última cotação: nº {ultima.id}, emitida em {formatarDataHora(ultima.emissao)}, válida até{" "}
						{formatarDataHora(paraCampoData(ultima.validade))}
					</span>
					{ultima.vencida && <span className={classes.vencida}>(vencida)</span>}
					<Button size="small" color="primary" startIcon={<OpenInNewIcon />} onClick={() => onAbrirCotacao(ultima.id)}>
						Abrir
					</Button>
				</div>
			)}

			<div className={classes.acoes}>
				<Button variant="contained" className={classes.botaoVerde} onMouseDown={semRoubarFoco}
					startIcon={<PictureAsPdfIcon />} disabled={ocupado} onClick={() => onGerar(datasCotacao())}>
					Gerar cotação
				</Button>
				{p.enviarCotacao && (
					<Button variant="contained" className={classes.botaoVerde} title="Envia o PDF da cotação no WhatsApp do cliente"
						onMouseDown={semRoubarFoco} startIcon={<WhatsAppIcon />} disabled={ocupado} onClick={() => onEnviar(datasCotacao())}>
						Enviar cotação
					</Button>
				)}
				{p.agendar && (
					<Button variant="contained" color="primary" startIcon={<EventIcon />} disabled={ocupado}
						onMouseDown={semRoubarFoco} onClick={() => onAgendar(datasAgendamento())}
						title="Move o lead para Agendamento (se preciso) e cria uma O.S. nova com os dados atuais">
						Agendar
					</Button>
				)}
			</div>

			{aviso && (
				<Alert severity={aviso.tipo} className={classes.avisoAcao}>
					{aviso.texto.split("\n").map((linha, n) => <div key={n}>{linha}</div>)}
				</Alert>
			)}
		</div>
	);
};
